import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { MissionTimelineDrawer } from "@/pages/studio/AIIntelligence";
import {
  ShieldCheck, Cpu, Workflow, Target, Zap, FileDown, Upload, LogIn, DollarSign,
  Mail, Database, GraduationCap, Users, Server, GitBranch, X, Clock, Search,
} from "lucide-react";

const cx = (...a) => a.filter(Boolean).join(" ");
const CAT_ICON = {
  Authentication: LogIn, AI: Cpu, Workflows: Workflow, Missions: Target, Automation: Zap,
  Export: FileDown, Training: GraduationCap, Memory: Database, Provider: Server,
  Billing: DollarSign, Email: Mail, Users: Users, Projects: Workflow, Strategists: Users,
  Administration: ShieldCheck, System: ShieldCheck, Evaluation: ShieldCheck,
};
const STATUS_CLS = {
  success: "text-emerald-300 border-emerald-500/30", failed: "text-red-300 border-red-500/30",
  warning: "text-amber-300 border-amber-500/30", skipped: "text-slate-400 border-white/10",
  waiting: "text-amber-300 border-amber-500/30", info: "text-slate-300 border-white/10",
};
const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtMs = (ms) => (ms == null ? null : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`);
const PRESETS = [["", "All time"], ["today", "Today"], ["yesterday", "Yesterday"], ["7d", "Last 7 Days"], ["month", "Last Month"]];

/**
 * ONE reusable Operational Intelligence timeline, powered entirely by the
 * centralized audit_events pipeline (Phase 4). No new storage, no new writes.
 * mode: "global" (admin /events) | "entity" (admin /timeline) | "portal" (client /portal/activity)
 */
export default function ActivityTimeline({
  title, subtitle, mode = "entity", params = {}, showFilters = false,
  limit = 25, compact = false, emptyText = "No activity yet.", testid = "activity-timeline",
}) {
  const [events, setEvents] = useState(null);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState({ categories: [] });
  const [f, setF] = useState({ q: "", category: "", preset: "" });
  const [openEvent, setOpenEvent] = useState(null);
  const [showHouse, setShowHouse] = useState(false);
  const isPortal = mode === "portal";
  const isGlobal = mode === "global";

  const load = useCallback(async () => {
    const base = isPortal ? "/portal/activity" : mode === "global" ? "/admin/audit/events" : "/admin/audit/timeline";
    const p = new URLSearchParams({ limit: String(limit) });
    Object.entries(params).forEach(([k, v]) => v && p.set(k, v));
    if (showFilters) Object.entries(f).forEach(([k, v]) => v && p.set(k, v));
    if (isGlobal && !showHouse && !f.category) p.set("exclude_severity", "low");
    const r = await api.get(`${base}?${p}`);
    setEvents(r.data.events || []);
    setTotal(r.data.total ?? (r.data.events || []).length);
  }, [mode, JSON.stringify(params), JSON.stringify(f), showFilters, limit, showHouse, isGlobal]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!showFilters) return;
    const metaPath = isPortal ? "/portal/activity/meta" : "/admin/audit/meta";
    api.get(metaPath).then((r) => setMeta(r.data)).catch(() => {});
  }, [showFilters, isPortal]);

  return (
    <div data-testid={testid}>
      {(title || showFilters) && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            {title && <h3 className="text-white text-sm font-semibold tracking-tight">{title}</h3>}
            {subtitle && <p className="text-slate-500 text-xs mt-0.5">{subtitle}</p>}
          </div>
          {showFilters && (
            <div className="flex flex-wrap gap-2">
              <select value={f.preset} onChange={(e) => setF({ ...f, preset: e.target.value })} data-testid={`${testid}-preset`}
                className="bg-navy border border-white/10 px-2.5 py-1.5 text-xs text-white rounded-sm focus:border-gold focus:outline-none">
                {PRESETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
              <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid={`${testid}-category`}
                className="bg-navy border border-white/10 px-2.5 py-1.5 text-xs text-white rounded-sm focus:border-gold focus:outline-none">
                <option value="">All</option>{(meta.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}</select>
              <div className="flex items-center gap-1.5"><Search size={13} className="text-slate-500" />
                <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search…" data-testid={`${testid}-search`}
                  className="bg-navy border border-white/10 px-2.5 py-1.5 text-xs text-white rounded-sm focus:border-gold focus:outline-none w-40" /></div>
              {isGlobal && (
                <button onClick={() => setShowHouse((v) => !v)} data-testid={`${testid}-toggle-housekeeping`}
                  className={cx("px-2.5 py-1.5 text-xs rounded-sm border transition-colors", showHouse ? "border-gold/50 text-gold bg-gold/10" : "border-white/10 text-slate-400 hover:text-white")}>
                  {showHouse ? "Hide system/debug" : "Show system/debug"}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {events === null ? (
        <div className="text-slate-500 text-sm py-6 text-center">Loading activity…</div>
      ) : events.length === 0 ? (
        <div className="text-slate-500 text-sm py-8 text-center border border-white/5 rounded-sm" data-testid={`${testid}-empty`}>{emptyText}</div>
      ) : (
        <ol className="relative border-l border-white/10 ml-2" data-testid={`${testid}-list`}>
          {events.map((e) => {
            const Icon = CAT_ICON[e.category] || ShieldCheck;
            const dur = fmtMs(e.execution_ms);
            return (
              <li key={e.id} className="mb-2 ml-4">
                <span className="absolute -left-[7px] flex items-center justify-center w-3.5 h-3.5 rounded-full bg-navy border border-gold/40">
                  <span className={cx("w-1.5 h-1.5 rounded-full", e.severity === "error" || e.severity === "critical" ? "bg-red-400" : e.severity === "warning" ? "bg-amber-400" : "bg-emerald-400")} />
                </span>
                <button onClick={() => setOpenEvent(e)} data-testid={`${testid}-event-${e.id}`}
                  className="w-full text-left flex items-start gap-3 rounded-sm px-3 py-2 hover:bg-white/5 transition-colors">
                  <Icon size={15} className="text-gold mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white text-sm">{e.action_type}</span>
                      <span className={cx("text-[10px] px-1.5 py-0.5 border rounded-sm", STATUS_CLS[e.status] || STATUS_CLS.info)}>{e.status}</span>
                    </div>
                    {!compact && (
                      <div className="text-slate-500 text-xs mt-0.5 truncate">
                        {e.actor_name || e.actor_email || "system"}
                        {e.entity_name ? ` · ${e.entity_name}` : ""}{e.department ? ` · ${e.department}` : ""}
                        {e.model ? ` · ${e.model}` : ""}{e.cost ? ` · $${e.cost.toFixed(4)}` : ""}{dur ? ` · ${dur}` : ""}
                      </div>
                    )}
                  </div>
                  <span className="text-[11px] text-slate-500 shrink-0">{fmt(e.timestamp)}</span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
      {mode !== "portal" && total > (events?.length || 0) && (
        <div className="text-[11px] text-slate-600 mt-2 ml-2">{events.length} of {total.toLocaleString()} events — open the Audit Center for the full history.</div>
      )}

      {openEvent && <EventDetail e={openEvent} isPortal={isPortal} onClose={() => setOpenEvent(null)} />}
    </div>
  );
}

function EventDetail({ e, isPortal, onClose }) {
  const [trace, setTrace] = useState(null);
  const [showTrace, setShowTrace] = useState(false);
  const [missionOpen, setMissionOpen] = useState(null);
  const missionId = e.mission || (e.entity_type === "mission" ? e.entity_id : null);
  const loadTrace = async () => {
    setShowTrace(true);
    if (!trace && e.correlation_id) {
      try { const r = await api.get(`/admin/audit/correlation/${e.correlation_id}`); setTrace(r.data); } catch { setTrace({ events: [] }); }
    }
  };
  const kv = [
    ["Who", e.actor_name || e.actor_email || "system"], ["When", fmt(e.timestamp)],
    ["Category", e.category], ["Action", e.action_type], ["Status", e.status],
    ["Module", e.module], ["Department", e.department],
    ["Entity", `${e.entity_type || ""} ${e.entity_name || e.entity_id || ""}`.trim()],
    ["Mission", e.mission], ["Workflow", e.workflow], ["Automation", e.automation],
    ["Provider", e.provider], ["Model", e.model], ["Tokens", e.tokens],
    ["Cost", e.cost != null ? `$${e.cost}` : null], ["Duration", fmtMs(e.execution_ms)],
    ["Reason", e.reason], ["Notes", e.notes],
  ].filter(([, v]) => v != null && v !== "");

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="activity-event-detail">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md bg-navy-light border-l border-white/10 h-full overflow-y-auto p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div><h3 className="text-white font-semibold">{e.action_type}</h3><p className="text-slate-500 text-xs">{e.category}</p></div>
          <button onClick={onClose} data-testid="activity-detail-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {kv.map(([l, v]) => <div key={l} className="border border-white/10 rounded-sm p-2"><div className="text-[10px] uppercase tracking-wide text-slate-500">{l}</div><div className="text-slate-200 text-xs mt-0.5 break-words">{String(v)}</div></div>)}
        </div>
        {!isPortal && (missionId || e.correlation_id) && (
          <div>
            <button onClick={() => missionId ? setMissionOpen(missionId) : loadTrace()} data-testid="activity-view-mission-timeline"
              className="flex items-center gap-1.5 w-full justify-center border border-gold/40 text-gold hover:bg-gold/10 px-3 py-2 text-xs rounded-sm">
              <GitBranch size={13} /> View Mission Timeline
            </button>
            {!missionId && showTrace && trace && (
              <div className="mt-2 space-y-1" data-testid="activity-trace">
                {(trace.events || []).length <= 1 ? <div className="text-slate-500 text-xs px-1">No linked events.</div> :
                  trace.events.map((t) => (
                    <div key={t.id} className={cx("flex items-center gap-2 text-xs border rounded-sm px-2.5 py-1.5", t.id === e.id ? "border-gold/50 bg-gold/5" : "border-white/10")}>
                      <Clock size={11} className="text-slate-500" /><span className="text-slate-300 truncate">{t.action_type}</span>
                      <span className="ml-auto text-slate-600">{fmt(t.timestamp)}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
      {missionOpen && <MissionTimelineDrawer openId={missionOpen} onClose={() => setMissionOpen(null)} />}
    </div>
  );
}
